// Judge calibration — the one screen that says whether the judge is an instrument.
//
// THE DIRECTION IS THE WHOLE POINT. The human verdicts are ground truth FOR THE
// JUDGE, not the reverse. A judge that cannot reproduce the human scores is not
// fit to judge anything, and every judge-derived number on the other tabs is
// worth exactly as much as this screen says it is.
//
// Three questions, three populations, each printed with its own n so they can
// never be read as one number:
//   1. judge vs the client's own P0 QA run   (the 16-case tablestake sheet)
//   2. judge vs the human labels the platform has stored  (/judge-accuracy)
//   3. judge vs the deterministic gate       (/judge-disagreement)
//
// The error directions are reported SEPARATELY because they are not
// interchangeable. A false accept (judge said pass, human said fail) rubber-stamps
// a real defect and inflates the score. A false reject (judge said fail, human
// said pass) invents a defect and sends the team to fix a non-problem.
//
// NOTHING HERE RENDERS 0 OR 100% FOR MISSING DATA. An unmeasured value is null
// and prints "not measured" — 0 means the judge got none right, null means
// nobody looked, and the two must never look the same.

import { useMemo } from "react";
import { http, qs } from "../api";
import { Badge, Empty, useAsync } from "../ui";
import { type SuiteDetail, type JudgeAccuracy, type JudgeDisagreement, type CasesResponse } from "./types";
import { dash, num, fmtPct, fmtInt, fmtNum3, Loadable, MetricCard, Card } from "./format";
import { TABLESTAKE_P0, type HumanVerdict } from "./tablestake";

// ------------------------------------------------------------------ the axis

/**
 * The comparison axis is PASS vs NOT-PASS, and `partial` counts as not-pass.
 * The tester wrote three levels; the judge emits two. Collapsing partial upward
 * would let a judge score full marks on a conversation the human called half
 * broken, which is the exact failure this screen exists to catch.
 */
function humanPassed(v: HumanVerdict): boolean {
  return v === "pass";
}

/** Tri-state on purpose: an absent, skipped or unrecognised verdict is NOT a fail. */
function judgePassed(raw: string | null | undefined): boolean | null {
  const s = String(raw ?? "").trim().toLowerCase();
  if (!s || s === "skip" || s === "skipped") return null;
  if (s === "correct" || s === "pass" || s === "passed") return true;
  if (s === "wrong" || s === "incorrect" || s === "fail" || s === "failed" || s === "partial") return false;
  return null;
}

/** A stored eval_labels verdict, mapped onto the sheet's three levels. */
function storedHumanVerdict(raw: string | null | undefined): HumanVerdict | null {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "correct" || s === "pass" || s === "passed") return "pass";
  if (s === "partial") return "partial";
  if (s === "wrong" || s === "incorrect" || s === "fail" || s === "failed") return "fail";
  return null;
}

/**
 * Cohen's kappa over the 2x2. Raw agreement flatters a judge on a skewed corpus:
 * if 90% of cases are failures, a judge that always says "fail" scores 90%
 * agreement and is worthless. Kappa corrects for the chance agreement the two
 * marginals produce on their own. Null when a marginal is degenerate — there is
 * no chance-corrected answer then, and 0 would read as "no better than guessing".
 *
 * Same formula the store uses for /judge-accuracy, so the two cards below cannot
 * disagree about what kappa means.
 */
function cohensKappa(pairs: Array<{ judge: boolean; human: boolean }>): number | null {
  const n = pairs.length;
  if (!n) return null;
  const agree = pairs.filter((p) => p.judge === p.human).length;
  const po = agree / n;
  const pj = pairs.filter((p) => p.judge).length / n;
  const ph = pairs.filter((p) => p.human).length / n;
  const pe = pj * ph + (1 - pj) * (1 - ph);
  return pe >= 1 ? null : (po - pe) / (1 - pe);
}

const VERDICT_TONE: Record<HumanVerdict, "good" | "warning" | "critical"> = {
  pass: "good",
  partial: "warning",
  fail: "critical",
};

interface CalibrationRow {
  case_id: string;
  scenario: string;
  lang: string;
  human: HumanVerdict;
  reason: string;
  /** "sheet" = the client's P0 QA run. "stored" = a label written from the Review tab. */
  source: "sheet" | "stored";
  judge_verdict: string | null;
  judge_reason: string | null;
  /** null = the judge never scored this case in this run, so there is nothing to agree with. */
  judge_pass: boolean | null;
}

// ------------------------------------------------------------------- the view

export function CalibrationTab({ detail, runId }: { detail: SuiteDetail | null; runId: string }) {
  const accuracy = useAsync<JudgeAccuracy>(
    () =>
      http.get<JudgeAccuracy>(`/chatbot/eval-ops/judge-accuracy${qs({ run_id: runId })}`).catch(() => ({})),
    [runId]
  );
  const disagreement = useAsync<JudgeDisagreement>(
    () =>
      runId
        ? http.get<JudgeDisagreement>(`/chatbot/eval-ops/judge-disagreement${qs({ run_id: runId })}`).catch(() => ({}))
        : Promise.resolve({}),
    [runId]
  );
  const cases = useAsync<CasesResponse>(
    () =>
      runId
        ? http
            .get<CasesResponse>(`/chatbot/eval-ops/runs/${encodeURIComponent(runId)}/cases`)
            .catch(() => ({ run_id: runId, cases: [] }))
        : Promise.resolve({ run_id: "", cases: [] }),
    [runId]
  );

  const scored = useMemo(() => {
    const out = new Map<string, { judge_verdict: string | null; judge_reason: string | null; human: string | null }>();
    for (const row of cases.data?.cases ?? []) {
      const id = String(row.case_id ?? "").trim();
      if (!id) continue;
      out.set(id, {
        judge_verdict: row.judge_verdict ?? null,
        judge_reason: row.judge_reason ?? null,
        human: row.human_verdict ?? null,
      });
    }
    return out;
  }, [cases.data]);

  // The sheet first, then any case in this run that carries a stored human label
  // and is not already on the sheet. Both are human verdicts; the source column
  // says which reviewer wrote them.
  const rows: CalibrationRow[] = useMemo(() => {
    const sheet: CalibrationRow[] = TABLESTAKE_P0.map((h) => {
      const hit = scored.get(h.case_id);
      return {
        ...h,
        source: "sheet",
        judge_verdict: hit?.judge_verdict ?? null,
        judge_reason: hit?.judge_reason ?? null,
        judge_pass: judgePassed(hit?.judge_verdict),
      };
    });
    const onSheet = new Set(TABLESTAKE_P0.map((h) => h.case_id));
    const stored: CalibrationRow[] = [];
    for (const [id, hit] of scored) {
      if (onSheet.has(id)) continue;
      const human = storedHumanVerdict(hit.human);
      if (!human) continue;
      stored.push({
        case_id: id,
        scenario: "",
        lang: "",
        human,
        reason: "",
        source: "stored",
        judge_verdict: hit.judge_verdict,
        judge_reason: hit.judge_reason,
        judge_pass: judgePassed(hit.judge_verdict),
      });
    }
    return [...sheet, ...stored];
  }, [scored]);

  // Only rows the judge actually scored are comparable. A case the run skipped is
  // excluded from every number below rather than counted as a disagreement — that
  // would blame the judge's reasoning for a case it never saw.
  const compared = useMemo(
    () =>
      rows
        .filter((r) => r.judge_pass !== null)
        .map((r) => ({ judge: r.judge_pass as boolean, human: humanPassed(r.human) })),
    [rows]
  );

  const nCompared = compared.length;
  const agreeN = compared.filter((p) => p.judge === p.human).length;
  const agreement = nCompared ? agreeN / nCompared : null;
  const kappa = cohensKappa(compared);
  const falseAccept = compared.filter((p) => p.judge && !p.human).length;
  const falseReject = compared.filter((p) => !p.judge && p.human).length;

  const sheetCounts = rows
    .filter((r) => r.source === "sheet")
    .reduce<Record<HumanVerdict, number>>(
      (acc, r) => ({ ...acc, [r.human]: acc[r.human] + 1 }),
      { pass: 0, partial: 0, fail: 0 }
    );

  const acc = accuracy.data;
  const storedN = num(acc?.n_compared);
  const dis = disagreement.data;
  const judge = detail?.judge ?? {};

  const notMeasured = nCompared
    ? null
    : `the judge scored none of these ${fmtInt(rows.length)} human-verdict cases in this run — nothing to compare`;

  return (
    <div className="stack">
      <Card
        title="Judge vs the client's P0 QA run"
        note={
          <>
            The human verdicts are ground truth FOR THE JUDGE, not the reverse. This is the client tester's own P0
            run — <span className="strong">{fmtInt(sheetCounts.pass)} pass</span>,{" "}
            <span className="strong">{fmtInt(sheetCounts.partial)} partial</span>,{" "}
            <span className="strong">{fmtInt(sheetCounts.fail)} fail</span> over{" "}
            {fmtInt(sheetCounts.pass + sheetCounts.partial + sheetCounts.fail)} cases — joined to this run by case id.
            The axis is pass vs not-pass and <span className="strong">partial counts as not-pass</span>. Computed in
            the browser from the sheet, which is why its n is stated on every card.
          </>
        }
      >
        <Loadable loading={cases.loading} error={cases.error} what="Failed to load the run's cases" cols={4}>
          <div className="grid c4">
            <MetricCard
              label="Agreement"
              value={fmtPct(agreement)}
              sub={`${fmtInt(agreeN)} of ${fmtInt(nCompared)} cases the judge scored`}
              sub2={`${fmtInt(rows.length - nCompared)} human-scored cases the judge did not reach`}
              tone={agreement !== null && agreement < 0.75 ? "bad" : "default"}
              note={notMeasured}
            />
            <MetricCard
              label="Cohen's kappa"
              value={fmtNum3(kappa)}
              sub="agreement above chance"
              sub2="raw agreement flatters a judge on a skewed corpus"
              tone={kappa !== null && kappa < 0.4 ? "bad" : "default"}
              note={
                notMeasured ??
                (kappa === null ? "undefined — one marginal is degenerate, and 0 would read as 'no better than chance'" : null)
              }
            />
            <MetricCard
              label="False accept"
              value={fmtInt(falseAccept)}
              sub="judge said pass, human said not-pass"
              sub2="the judge rubber-stamped a real defect"
              tone={falseAccept > 0 ? "bad" : "default"}
              note={notMeasured}
            />
            <MetricCard
              label="False reject"
              value={fmtInt(falseReject)}
              sub="judge said not-pass, human said pass"
              sub2="the judge invented a defect"
              tone={falseReject > 0 ? "warn" : "default"}
              note={notMeasured}
            />
          </div>
        </Loadable>
      </Card>

      <Card
        title="Case by case — human verdict, judge verdict, and whether they agree"
        note="Every case carries the tester's own written reason. A row the judge never scored shows a dash, not a disagreement."
      >
        <Loadable loading={cases.loading} error={cases.error} what="Failed to load the run's cases" cols={6}>
          {!rows.length ? (
            <Empty title="No human-scored cases" body="Nothing to calibrate the judge against." />
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Case</th>
                    <th>Lang</th>
                    <th>Human</th>
                    <th>Judge</th>
                    <th>Agree</th>
                    <th>Human reason</th>
                    <th>Judge reason</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const agree = row.judge_pass === null ? null : row.judge_pass === humanPassed(row.human);
                    const direction =
                      row.judge_pass === null
                        ? ""
                        : agree
                          ? ""
                          : row.judge_pass
                            ? "false accept"
                            : "false reject";
                    return (
                      <tr key={`${row.source}-${row.case_id}`}>
                        <td className="truncate" style={{ maxWidth: 220 }} title={row.case_id}>
                          {row.case_id}
                          {row.scenario && <div className="muted" style={{ fontSize: 10 }}>{row.scenario}</div>}
                          {row.source === "stored" && (
                            <div className="muted" style={{ fontSize: 10 }}>
                              stored label
                            </div>
                          )}
                        </td>
                        <td>{dash(row.lang)}</td>
                        <td>
                          <Badge tone={VERDICT_TONE[row.human]}>{row.human}</Badge>
                        </td>
                        <td>
                          {row.judge_pass === null ? (
                            <span className="muted" title={dash(row.judge_verdict)}>
                              not scored
                            </span>
                          ) : (
                            <Badge tone={row.judge_pass ? "good" : "critical"}>{dash(row.judge_verdict)}</Badge>
                          )}
                        </td>
                        <td>
                          {agree === null ? (
                            <span className="muted">—</span>
                          ) : agree ? (
                            <span style={{ color: "var(--success)" }}>agree</span>
                          ) : (
                            <span style={{ color: "var(--critical)" }}>{direction}</span>
                          )}
                        </td>
                        <td className="truncate" style={{ maxWidth: 320 }} title={row.reason}>
                          {dash(row.reason)}
                        </td>
                        <td className="truncate muted" style={{ maxWidth: 260 }} title={row.judge_reason ?? ""}>
                          {dash(row.judge_reason)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Loadable>
      </Card>

      <Card
        title="Judge vs the human labels the platform has stored"
        note="The server's own measurement over eval_labels — a different population from the sheet above, so it carries its own n and is never folded into it. Labels are written from the Review tab."
      >
        <Loadable loading={accuracy.loading} error={accuracy.error} what="Failed to load judge accuracy" cols={4}>
          <div className="grid c4">
            <MetricCard
              label="Agreement"
              value={storedN ? fmtPct(acc?.agreement) : null}
              sub={`${fmtInt(acc?.n_compared)} of ${fmtInt(acc?.n_labeled)} labelled cases compared`}
              sub2={
                acc?.min_n === null || acc?.min_n === undefined
                  ? undefined
                  : acc?.reliable
                    ? "sample clears the minimum"
                    : `below the ${fmtInt(acc?.min_n)}-label minimum`
              }
              tone={acc?.min_n !== undefined && acc?.min_n !== null && !acc?.reliable ? "warn" : "default"}
              note={
                storedN
                  ? null
                  : "not measured — no case in this scope carries both a judge verdict and a stored human label"
              }
            />
            <MetricCard
              label="Cohen's kappa"
              value={storedN ? fmtNum3(acc?.kappa) : null}
              sub="agreement above chance"
              note={
                !storedN
                  ? "not measured"
                  : acc?.kappa === null || acc?.kappa === undefined
                    ? "undefined — one marginal is degenerate"
                    : null
              }
            />
            <MetricCard
              label="False accept"
              value={storedN ? fmtInt(acc?.false_accept) : null}
              sub="judge said correct, human said wrong"
              tone={storedN && num(acc?.false_accept) > 0 ? "bad" : "default"}
              note={storedN ? null : "not measured"}
            />
            <MetricCard
              label="False reject"
              value={storedN ? fmtInt(acc?.false_reject) : null}
              sub="judge said wrong, human said correct"
              sub2={`${fmtInt(acc?.judge_skips)} judge skips excluded`}
              tone={storedN && num(acc?.false_reject) > 0 ? "warn" : "default"}
              note={storedN ? null : "not measured"}
            />
          </div>
        </Loadable>
      </Card>

      <Card
        title="Judge vs the deterministic gate"
        note="A third pair, and the weakest evidence of the three: the gate is not a human. It is here because a judge that also disagrees with the gate everywhere is broken in a way the small human sample cannot prove on its own. The turns behind it are the Review tab."
      >
        <Loadable loading={disagreement.loading} error={disagreement.error} what="Failed to load judge disagreement" cols={4}>
          <div className="grid c4">
            <MetricCard
              label="Agreement"
              value={num(dis?.judged_n) ? fmtPct(dis?.agreement) : null}
              sub={`${fmtInt(dis?.agree)} of ${fmtInt(dis?.judged_n)} judged turns`}
              note={num(dis?.judged_n) ? null : "no turn in this run carries a judge verdict"}
            />
            <MetricCard
              label="False pass"
              value={num(dis?.judged_n) ? fmtInt(dis?.false_pass) : null}
              sub="the suite is certifying these green"
              tone={num(dis?.false_pass) > 0 ? "bad" : "default"}
              note={num(dis?.judged_n) ? null : "not measured"}
            />
            <MetricCard
              label="False fail"
              value={num(dis?.judged_n) ? fmtInt(dis?.false_fail) : null}
              sub="candidate over-strict checks"
              tone={num(dis?.false_fail) > 0 ? "warn" : "default"}
              note={num(dis?.judged_n) ? null : "not measured"}
            />
            <MetricCard
              label="Skipped · unjudged"
              value={`${fmtInt(dis?.skipped)} · ${fmtInt(dis?.unjudged)}`}
              sub="excluded from agreement"
              hint="A judge skip is an infrastructure failure, not a disagreement, so it never counts against the judge's reasoning."
            />
          </div>
          <div className="row" style={{ flexWrap: "wrap", gap: "var(--sp-4)", marginTop: "var(--sp-3)", fontSize: 12 }}>
            <span className="muted">
              macro-F1 <span className="strong num">{fmtNum3(judge.macro_f1)}</span> over {fmtInt(judge.n_segments)} row
              segments
            </span>
            <span className="muted">
              weighted <span className="num">{fmtNum3(judge.weighted_f1)}</span>
            </span>
            <span className="muted">
              micro <span className="num">{fmtNum3(judge.micro_f1)}</span>
            </span>
            <span className="muted">
              precision <span className="num">{fmtNum3(judge.precision)}</span>
            </span>
            <span className="muted">
              recall <span className="num">{fmtNum3(judge.recall)}</span>
            </span>
          </div>
          {judge.definition && (
            <p className="muted" style={{ fontSize: 11, marginTop: "var(--sp-2)" }}>
              {judge.definition}
            </p>
          )}
        </Loadable>
      </Card>
    </div>
  );
}
